import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { startFormLoading, stopFormLoading } from 'src/store/actions/loader';
import { saveFaqsNotesDocuments } from 'src/store/actions/faqsNotesDocuments';
import {
  DocumentApiData,
  DocumentFormData,
  prepareDocumentForApiUploadToS3,
} from 'src/utils/S3UploadDocumentsUtility';

interface UseDocumentSaveOptions {
  onSuccess?: () => void;
  additionalData?: Record<string, any>;
  customErrorMessage?: string;
}

export interface UseDocumentSaveReturn {
  saveDocument: (
    document: DocumentFormData,
    itemId: string,
    userId: string,
    dynamicAdditionalData?: Record<string, any>
  ) => Promise<void>;
  isSaving: boolean;
}

export const useDocumentSave = (
  options: UseDocumentSaveOptions = {}
): UseDocumentSaveReturn => {
  const dispatch = useDispatch();
  const { onSuccess, additionalData = {}, customErrorMessage } = options;

  const saveDocument = async (
    document: DocumentFormData,
    itemId: string,
    userId: string,
    dynamicAdditionalData?: Record<string, any>
  ): Promise<void> => {
    try {
      dispatch(startFormLoading());

      const apiData: DocumentApiData = await prepareDocumentForApiUploadToS3(
        document,
        itemId,
        userId,
        { ...additionalData, ...dynamicAdditionalData }
      );

      if (!apiData) {
        dispatch(stopFormLoading());
        toast.error(
          customErrorMessage ||
            'Failed to prepare this document for upload. Please try again or contact Juniper support if the issue persists.',
          { autoClose: 12000, position: 'top-center' }
        );
        return;
      }

      dispatch(saveFaqsNotesDocuments(apiData));

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      dispatch(stopFormLoading());
      toast.error(
        customErrorMessage ||
          'Document upload failed. Please check your connection and try again or contact Juniper support if the issue persists.',
        { autoClose: 12000, position: 'top-center' }
      );
      console.error('Document upload error:', error);
    }
  };

  return {
    saveDocument,
    isSaving: false,
  };
};
